import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { ConfirmDialog } from '../components/ui';

type InterestSettings = { suggestions: string[]; allow_custom: boolean };

export default function EventInterestSettingsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [topics, setTopics] = useState('');
  const [allowCustom, setAllowCustom] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    apiClient.get<InterestSettings>(`/admin/events/${eventId}/interest-settings`)
      .then((settings) => { setTopics(settings.suggestions.join('\n')); setAllowCustom(settings.allow_custom); })
      .catch(() => toast.error('Could not load interest settings'));
  }, [eventId]);

  async function save() {
    if (!eventId) return;
    setSaving(true);
    const suggestions = [...new Set(topics.split(/\r?\n|,/).map((topic) => topic.trim()).filter(Boolean))];
    try {
      await apiClient.put(`/admin/events/${eventId}/interest-settings`, { suggestions, allow_custom: allowCustom });
      toast.success('Interest settings saved');
      setConfirmSaveOpen(false);
    } catch {
      toast.error('Could not save interest settings');
    } finally {
      setSaving(false);
    }
  }

  return <div className="max-w-3xl p-8">
    <div className="mb-6 flex items-start justify-between"><div><h1 className="text-2xl font-semibold text-white">Event interests</h1><p className="mt-1 text-sm text-gray-400">Set optional, event-specific topics for attendees. Their global profile remains unchanged.</p></div><Link to="/events" className="text-sm text-sky-400">Back to events</Link></div>
    <section className="rounded-xl border border-gray-800 bg-gray-900 p-5">
      <label className="block text-sm font-medium text-white">Suggested topics<textarea aria-label="Suggested topics" value={topics} onChange={(event) => setTopics(event.target.value)} placeholder={'Mechanical Engineering\nMachine Learning'} className="mt-2 min-h-40 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white" /></label>
      <p className="mt-2 text-xs text-gray-500">Enter one topic per line (or separate them with commas).</p>
      <label className="mt-5 flex items-center gap-2 text-sm text-gray-200"><input type="checkbox" checked={allowCustom} onChange={(event) => setAllowCustom(event.target.checked)} /> Allow attendees to add their own topics</label>
      <button type="button" onClick={() => setConfirmSaveOpen(true)} disabled={saving} className="mt-6 rounded bg-sky-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">Save interest settings</button>
    </section>
    <ConfirmDialog open={confirmSaveOpen} onClose={() => setConfirmSaveOpen(false)} onConfirm={save} loading={saving} intent="primary" title="Save changes?" description="Apply the current interest settings?" confirmLabel="Save changes" />
  </div>;
}
